import React, { useState } from "react";
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Check as CheckIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { SPORTS, SPORT_BY_ID } from "../constants/sportWorkspace";
import { SPORT_ICONS } from "./sportIcons";
import { brandRgba } from "../utils/brandAssets";

const SportGlyph = ({ sportId, size = 22 }) => {
  const sport = SPORT_BY_ID[sportId];
  const Icon = SPORT_ICONS[sportId];
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: brandRgba(sport.accent, 0.16),
        color: sport.accent,
      }}
    >
      <Icon sx={{ fontSize: size }} />
    </Box>
  );
};

const SportSwitcher = ({
  currentSport,
  isRailCollapsed,
  onSelect,
  onOpenChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const current = SPORT_BY_ID[currentSport];
  const CurrentIcon = SPORT_ICONS[currentSport];

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onOpenChange?.(false);
  };

  const handleSelect = (sportId) => {
    handleClose();
    if (sportId !== currentSport) onSelect?.(sportId);
  };

  const trigger = (
    <Box
      component="button"
      type="button"
      id="sport-switcher-button"
      aria-label={`Switch league, ${current.label} selected`}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? "sport-switcher-menu" : undefined}
      onClick={handleOpen}
      sx={{
        appearance: "none",
        border: `1px solid ${
          open ? brandRgba(current.accent, 0.45) : theme.palette.divider
        }`,
        backgroundColor: open
          ? brandRgba(current.accent, 0.1)
          : "rgba(255, 255, 255, 0.03)",
        color: theme.palette.text.primary,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: isRailCollapsed ? "center" : "flex-start",
        gap: 1.25,
        px: isRailCollapsed ? 0 : 1.25,
        py: isRailCollapsed ? 0.75 : 1,
        borderRadius: 1.5,
        cursor: "pointer",
        textAlign: "left",
        transition:
          "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open ? `0 0 0 1px ${brandRgba(current.accent, 0.2)}` : "none",
        "&:hover": {
          borderColor: brandRgba(current.accent, 0.4),
          backgroundColor: brandRgba(current.accent, 0.08),
        },
      }}
    >
      {isRailCollapsed ? (
        <CurrentIcon sx={{ fontSize: 22, color: current.accent }} />
      ) : (
        <>
          <SportGlyph sportId={currentSport} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.82rem",
                letterSpacing: "0.08em",
                lineHeight: 1.2,
              }}
            >
              {current.label}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.72rem",
                lineHeight: 1.2,
              }}
            >
              {current.name}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "text.secondary",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </>
      )}
    </Box>
  );

  return (
    <>
      {isRailCollapsed ? (
        <Tooltip title={`${current.label} · switch league`} placement="right" arrow>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <Menu
        id="sport-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={
          isRailCollapsed
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "left" }
        }
        transformOrigin={
          isRailCollapsed
            ? { vertical: "top", horizontal: "left" }
            : { vertical: "top", horizontal: "left" }
        }
        disableScrollLock
        MenuListProps={{
          "aria-labelledby": "sport-switcher-button",
          sx: { py: 0.75 },
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            minWidth: isRailCollapsed ? 240 : 218,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          },
        }}
      >
        {SPORTS.map((sport) => {
          const selected = sport.id === currentSport;
          const Icon = SPORT_ICONS[sport.id];
          return (
            <MenuItem
              key={sport.id}
              selected={selected}
              onClick={() => handleSelect(sport.id)}
              sx={{
                mx: 0.5,
                my: 0.25,
                px: 1.25,
                py: 1.15,
                borderRadius: 1.5,
                gap: 1.25,
                "&.Mui-selected": {
                  backgroundColor: brandRgba(sport.accent, 0.12),
                },
                "&.Mui-selected:hover": {
                  backgroundColor: brandRgba(sport.accent, 0.18),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: sport.accent }}>
                <Icon />
              </ListItemIcon>
              <ListItemText
                primary={sport.label}
                secondary={sport.name}
                primaryTypographyProps={{
                  fontWeight: selected ? 700 : 600,
                  fontSize: "0.9rem",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.72rem",
                }}
              />
              {selected && (
                <CheckIcon sx={{ fontSize: 18, color: sport.accent }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default SportSwitcher;
